import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import { CloudUpload, CheckCircle, Error } from '@mui/icons-material';
import { api } from '../services/api';

interface FileUploadProps {
  onAnalysisComplete: (data: any, reportId: string) => void;
  onAnalysisStart: () => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ onAnalysisComplete, onAnalysisStart }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [processingStage, setProcessingStage] = useState<string>('');

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file: File) => {
    // Accept both PDF and image files
    const validTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/bmp'
    ];
    
    if (!validTypes.includes(file.type)) {
      setError('Please upload a PDF or image file (JPG, PNG, GIF, WEBP, BMP)');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);
    setProcessingStage('Uploading file...');

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Stage 1: Upload and extract
      setProcessingStage(file.type.startsWith('image/') ? 'Extracting text from image...' : 'Extracting text from PDF...');
      const response = await api.uploadPdf(file);
      
      setProcessingStage('Extracting biomarkers...');
      setUploadResult(response.data);

      // The new API response already includes biomarker_data, so we can directly pass it
      if (response.data.biomarker_data) {
        // Start analysis loading state
        onAnalysisStart();
        
        // Stage 2: Analyze biomarkers
        setProcessingStage('Analyzing biomarkers with AI...');
        
        try {
          const analysisResponse = await api.analyzeBiomarkers(response.data.biomarker_data);
          const analysisData = analysisResponse.data.analysis;
          
          setProcessingStage('Analysis complete!');
          setTimeout(() => {
            onAnalysisComplete(analysisData, response.data.report_id);
            
            // Show success message
            setUploadResult({
              ...response.data,
              analysis_complete: true,
              message: "Analysis complete! Check the Analysis Results tab for detailed insights."
            });
            setProcessingStage('');
          }, 300);
          
        } catch (error) {
          console.error('Error analyzing biomarkers:', error);
          setProcessingStage('Preparing results...');
          
          // Fallback to basic transformation if analysis fails
          const biomarkers = response.data.biomarker_data.Biomarkers.map((bio: any) => ({
            name: bio.Biomarker,
            value: bio.Value,
            unit: bio.Unit,
            used_range: bio.Reference_Range,
            status: 'NORMAL', // Will be determined in AnalysisDisplay component
            rag: null,
            gpt_check: null,
            validation_aligned: true
          }));

          const analysisData = {
            biomarkers,
            summary_counts: {
              total: biomarkers.length,
              normal: biomarkers.length, // Will be recalculated in AnalysisDisplay
              abnormal: 0 // Will be recalculated in AnalysisDisplay
            },
            age: response.data.biomarker_data.Age,
            gender: response.data.biomarker_data.Gender
          };
          
          setTimeout(() => {
            onAnalysisComplete(analysisData, response.data.report_id);
            setUploadResult({
              ...response.data,
              analysis_complete: true,
              message: "Analysis complete! Check the Analysis Results tab for detailed insights."
            });
            setProcessingStage('');
          }, 300);
        }
      }

    } catch (err: any) {
      setError(err.response?.data?.detail || 'Upload failed');
      setUploadProgress(0);
      setProcessingStage('');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom align="center">
        Upload Blood Test Report
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Upload a PDF or image (JPG, PNG) of your blood test report to get AI-powered biomarker analysis
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: 'center',
          border: dragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
          backgroundColor: dragActive ? '#f5f5f5' : 'transparent',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-input')?.click()}
      >
        <input
          id="file-input"
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.bmp,image/*"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        
        <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        
        <Typography variant="h6" gutterBottom>
          {dragActive ? 'Drop your file here' : 'Drag & drop your report here'}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Supports PDF and images (JPG, PNG, GIF, WEBP, BMP)
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          or click to browse files
        </Typography>
        
        <Button variant="contained" component="span" disabled={uploading}>
          Choose File
        </Button>
      </Paper>

      {uploading && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, color: 'primary.main' }}>
            {processingStage || 'Processing...'}
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Error sx={{ mr: 1 }} />
          {error}
        </Alert>
      )}

      {uploadResult && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CheckCircle color="success" sx={{ mr: 1 }} />
              <Typography variant="h6">
                {uploadResult.analysis_complete ? 'Analysis Complete!' : 'Upload Successful!'}
              </Typography>
            </Box>
            
            {uploadResult.analysis_complete && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {uploadResult.message}
              </Alert>
            )}
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Report ID: {uploadResult.report_id}
            </Typography>

            {uploadResult.biomarker_data && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Extracted Information:
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Age" 
                      secondary={uploadResult.biomarker_data.Age || 'Not specified'} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Gender" 
                      secondary={uploadResult.biomarker_data.Gender || 'Not specified'} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Biomarkers Found" 
                      secondary={`${uploadResult.biomarker_data.Biomarkers?.length || 0} biomarkers`} 
                    />
                  </ListItem>
                </List>

                {uploadResult.biomarker_data.Biomarkers && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Sample Biomarkers:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {uploadResult.biomarker_data.Biomarkers.slice(0, 5).map((bio: any, index: number) => (
                        <Chip
                          key={index}
                          label={`${bio.Biomarker}: ${bio.Value} ${bio.Unit || ''}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {uploadResult.biomarker_data.Biomarkers.length > 5 && (
                        <Chip
                          label={`+${uploadResult.biomarker_data.Biomarkers.length - 5} more`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FileUpload;
